import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Target, Heart, ArrowRight, Baby, GraduationCap, Home, TrendingUp, Shield, AlertTriangle, Award, DollarSign } from "lucide-react";

function Community() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const crisisStats = [
    {
      icon: AlertTriangle,
      number: "85%",
      label: "Poverty Rate",
      description: "Families unable to meet basic needs in Kayunga",
      color: "from-red-500 to-orange-500"
    },
    {
      icon: Baby,
      number: "40/100",
      label: "Infant Mortality",
      description: "Deaths per 100 births due to lack of supplies",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Home,
      number: "Rural",
      label: "Most Vulnerable",
      description: "Adolescent mothers in remote areas",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Heart,
      number: "2021",
      label: "Initiative Launch",
      description: "Dedicated response to maternal health crisis",
      color: "from-green-500 to-emerald-500"
    }
  ];

  const solutionAreas = [
    {
      icon: GraduationCap,
      title: "Vocational Skills Training",
      description: "Equipping women with practical skills to produce essential mama kits for safe childbirth",
      color: "from-purple-500 to-pink-500",
      impact: "Skill Empowerment"
    },
    {
      icon: DollarSign,
      title: "Income Generation",
      description: "Enabling women to generate sustainable income through mama kit production and sales",
      color: "from-green-500 to-emerald-500",
      impact: "Economic Independence"
    },
    {
      icon: Shield,
      title: "Cost Reduction",
      description: "Significantly reducing expenses for families by producing kits locally within the community",
      color: "from-blue-500 to-cyan-500",
      impact: "Financial Relief"
    },
    {
      icon: TrendingUp,
      title: "Community Development",
      description: "Creating lasting impact through knowledge transfer and household income improvement",
      color: "from-orange-500 to-red-500",
      impact: "Sustainable Change"
    }
  ];

  const outcomes = [
    { icon: Baby, metric: "↓50%", label: "Mortality Reduction", description: "Target reduction in infant deaths" },
    { icon: Users, metric: "200+", label: "Women Trained", description: "Skilled mama kit producers" },
    { icon: Home, metric: "1000+", label: "Families Served", description: "Access to essential supplies" }
  ];

  const galleryImages = [
    { src: "img/maama/0-min.jpg", title: "Mama Kit Production Training" },
    { src: "img/maama/1-min.jpg", title: "Skills Development Workshop" },
    { src: "img/maama/2-min.jpg", title: "Community Empowerment" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-rose-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-40 pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-pink-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-rose-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
          
          {/* Decorative maternal health symbols */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-5">
            <svg viewBox="0 0 100 100" className="w-full h-full text-pink-600 animate-pulse">
              <circle cx="50" cy="40" r="15" fill="currentColor"/>
              <ellipse cx="50" cy="65" rx="20" ry="25" fill="currentColor"/>
              <circle cx="50" cy="85" r="8" fill="currentColor"/>
            </svg>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div 
            id="hero-content"
            data-animate
            className={`text-center transform transition-all duration-1000 ${
              isVisible['hero-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-pink-100 text-pink-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Baby className="w-4 h-4 mr-2" />
              Maternal Health & Economic Empowerment
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-8">
              <span className="bg-gradient-to-r from-pink-600 via-purple-600 to-rose-600 bg-clip-text text-transparent">
                Kayunga Community
              </span>
              <br />
              <span className="text-gray-800">Maama Kit Project</span>
            </h1>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-xl border border-pink-100">
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 p-6 rounded-r-2xl mb-6">
                  <p className="text-lg text-red-800 font-semibold">
                    <strong>Crisis Alert:</strong> With 85% poverty rates and 40 infant deaths per 100 births, 
                    Kayunga faces a maternal health emergency requiring immediate intervention.
                  </p>
                </div>
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
                  Established in <strong className="text-pink-700">2021</strong>, our initiative empowers women 
                  with vocational skills to produce essential mama kits, reducing both maternal mortality 
                  and household expenses while creating sustainable income opportunities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crisis Statistics Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="crisis-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['crisis-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              The Maternal Health <span className="bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent">Crisis</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the urgent need for intervention in Kayunga's maternal health landscape
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-red-500 to-pink-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {crisisStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  id={`crisis-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`crisis-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-red-500">
                    <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-gray-800 mb-2">{stat.number}</div>
                      <div className="text-lg font-semibold text-red-600 mb-2">{stat.label}</div>
                      <div className="text-sm text-gray-600">{stat.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Solution Areas Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="solution-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['solution-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Our <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Solution</span>
            </h2>
            <p className="text-xl text-gray-600">Comprehensive approach to maternal health and women's empowerment</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutionAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <div
                  key={index}
                  id={`solution-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 300} ${
                    isVisible[`solution-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full">
                    <div className={`w-20 h-20 bg-gradient-to-r ${area.color} rounded-3xl flex items-center justify-center mb-6`}>
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">{area.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{area.description}</p>
                    
                    <div className={`inline-flex items-center bg-gradient-to-r ${area.color} bg-opacity-10 px-4 py-2 rounded-full`}>
                      <div className={`w-3 h-3 bg-gradient-to-r ${area.color} rounded-full mr-3`}></div>
                      <span className="font-semibold text-gray-800">{area.impact}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Impact Metrics */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="impact-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['impact-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Projected <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Impact</span>
            </h2>
            <p className="text-xl text-gray-600">Measurable outcomes from our maternal health intervention</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((outcome, index) => {
              const Icon = outcome.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-pink-600 to-purple-600 rounded-3xl p-8 text-white text-center transform hover:scale-105 transition-all duration-300"
                >
                  <Icon className="w-12 h-12 mx-auto mb-4" />
                  <div className="text-4xl font-black mb-2">{outcome.metric}</div>
                  <div className="text-xl font-semibold mb-2">{outcome.label}</div>
                  <div className="text-pink-100">{outcome.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Project Mission */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div 
            id="mission"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['mission'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-3xl p-1 shadow-2xl">
              <div className="bg-white rounded-3xl p-8 md:p-12 text-center">
                <Award className="w-20 h-20 text-pink-600 mx-auto mb-6" />
                <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  Our <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Mission</span>
                </h3>
                <p className="text-xl text-gray-700 leading-relaxed">
                  The core objective of this project is to <strong>enrich the community with knowledge and practical skills</strong> 
                  that not only improve the quality of life but also serve as a catalyst for boosting household income. 
                  As we continue our efforts, we aspire to make a lasting impact, transforming the lives of women 
                  and their families in Kayunga and contributing to the broader development of our community.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <div className="py-20 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Project <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Documentation</span>
            </h2>
            <p className="text-xl text-gray-600">Empowering women through skills development and maternal health support</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {galleryImages.map((image, index) => (
              <div key={index} className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="font-semibold">{image.title}</div>
                    <div className="text-sm text-gray-200">Maama Kit Initiative</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-pink-600 via-purple-600 to-rose-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <Heart className="w-5 h-5 mr-3" />
              Save Lives • Empower Women • Transform Communities
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Join the Maternal Health Revolution
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Support our Maama Kit Project and help us save lives while empowering women with 
              sustainable income opportunities. Together, we can reduce infant mortality rates 
              and create lasting change in Kayunga's most vulnerable communities.
            </p>
            
            <button 
              onClick={() => window.location.href = '/fund-our-work'}
              className="bg-white text-pink-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
            >
              Save Lives Today <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Community;